import { type ReactElement, useEffect, useState } from 'react';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { Badge, Button, Notice, PageHeader, Panel, SectionTitle, Stack, Measure } from '@vello/web-theme';
import { ADMIN_BUILD } from '../version';
import { DetailRow as Row } from '../components/detail-row';

/**
 * Configurações › Sobre (D322): the build identity of this Admin bundle (release tag + git commit + build time)
 * next to the version of the Vello API it is connected to, so the operator can confirm exactly which version is
 * running and match it against what was built and deployed. The commit is the strongest signal — it maps 1:1 to
 * the source. The API version is read from the public `/v1/version` endpoint (no auth), with a graceful failure.
 */

interface ApiVersion {
  readonly version?: string;
  readonly commit?: string;
  readonly service?: string;
  readonly name?: string;
}

type ApiState = { readonly kind: 'loading' } | { readonly kind: 'ok'; readonly data: ApiVersion } | { readonly kind: 'error' };

export function AboutScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const [api, setApi] = useState<ApiState>({ kind: 'loading' });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10_000);
    void (async () => {
      try {
        const res = await fetch(`${runtime.apiBaseUrl}/v1/version`, { signal: controller.signal, headers: { accept: 'application/json' } });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as ApiVersion;
        setApi({ kind: 'ok', data });
      } catch {
        setApi({ kind: 'error' });
      } finally {
        window.clearTimeout(timer);
      }
    })();
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [runtime.apiBaseUrl]);

  const apiVersion = api.kind === 'ok' ? (api.data.version ?? '') : '';
  const apiCommit = api.kind === 'ok' ? (api.data.commit ?? '') : '';
  const apiService = api.kind === 'ok' ? (api.data.service ?? api.data.name ?? '') : '';

  const onCopy = async (): Promise<void> => {
    const lines = [
      `admin.version=${ADMIN_BUILD.version}`,
      `admin.commit=${ADMIN_BUILD.commit}`,
      `admin.builtAt=${ADMIN_BUILD.builtAt}`,
      `api.endpoint=${runtime.apiBaseUrl}`,
      `api.version=${apiVersion || 'unknown'}`,
      `api.commit=${apiCommit || 'unknown'}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (permissions/insecure context) — the values stay visible on screen to copy by hand.
    }
  };

  const apiStatus =
    api.kind === 'loading' ? (
      <Badge tone="neutral">{t('about.api.loading')}</Badge>
    ) : api.kind === 'error' ? (
      <Badge tone="danger">{t('about.api.error')}</Badge>
    ) : (
      <Badge tone="success">{t('about.api.online')}</Badge>
    );

  return (
    // The header and the cards share one narrow frame, so "Copy" sits at the right edge of the content it belongs to.
    <Measure size="lg">
      <PageHeader
        title={t('about.title')}
        subtitle={t('about.subtitle')}
        actions={
          <Button variant="secondary" onClick={() => void onCopy()}>
            {copied ? t('about.copied') : t('about.copy')}
          </Button>
        }
      />

      <Stack gap={6}>
        <Panel>
          <SectionTitle>
            {t('about.panel.admin')}
          </SectionTitle>
          <Row label={t('about.field.version')} value={ADMIN_BUILD.version} mono />
          <Row label={t('about.field.commit')} value={ADMIN_BUILD.commit} mono />
          <Row label={t('about.field.builtAt')} value={ADMIN_BUILD.builtAt} mono />
          <Row label={t('about.field.apiEndpoint')} value={runtime.apiBaseUrl} mono />
        </Panel>

        <Panel>
          <SectionTitle>
            {t('about.panel.api')}
          </SectionTitle>
          <Row label={t('about.field.status')} value={apiStatus} mono={false} />
          <Row label={t('about.field.version')} value={apiVersion} mono />
          <Row label={t('about.field.commit')} value={apiCommit} mono />
          <Row label={t('about.field.service')} value={apiService} mono />
        </Panel>

        <Notice tone="neutral">{t('about.note')}</Notice>
      </Stack>
    </Measure>
  );
}
